import axios from "axios";

// Use proxy route instead of direct backend URL
// Proxy route handles internal backend URL server-side
const API_BASE_URL = "/api/proxy/internal";

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// Attach token from localStorage to every request
axiosInstance.interceptors.request.use(
  (config) => {
    if (typeof window !== "undefined") {
      const token = localStorage.getItem("token");
      const language = localStorage.getItem("i18nextLng");
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
        config.headers["Accept-Language"] = language;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Only an AUTHENTICATION failure may end the session.
 *
 * This interceptor used to log out on ANY 401. One forbidden resource therefore
 * destroyed the whole session, which is how a coach failure was eventually traced to
 * a scenarios/training route four weeks after the symptom first appeared
 * (docs/20260901-COACH-SPEC-401-LOGOUT-ROOT-CAUSE.md).
 *
 * The discriminator is `code`, set by the backend's authenticateToken middleware and
 * always prefixed `AUTH_` (AUTH_TOKEN_MISSING / _INVALID / _EXPIRED / AUTH_SESSION_REVOKED).
 * Matching on the prefix rather than on message prose is the whole point: the messages
 * are prose, and prose is what made the original bug possible.
 *
 * AUTH_TOKEN_EXPIRED deliberately still logs out. For an idle LTI learner whose one-hour
 * token lapsed while a TALK sat open, that redirect is the only recovery — leaving them
 * on a dead page would be a smaller harm than today's, but still a harm.
 *
 * A 401 without an AUTH_ code is not about the token, so it is surfaced to the caller
 * like any other error and the session is left alone.
 */
const isAuthenticationFailure = (error: unknown): boolean => {
  const response = (error as { response?: { data?: { code?: unknown } } })?.response;
  const code = response?.data?.code;
  return typeof code === "string" && code.startsWith("AUTH_");
};

// Add response interceptor to handle 401 responses
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && isAuthenticationFailure(error)) {
      try {
        // Send logout event to the app
        window.dispatchEvent(new Event("auth:logout"));
      } catch (logoutError) {
        // Even if logout fails, we still want to clear local storage
        console.error("Logout failed:", logoutError);
      }
    }
    if (axios.isAxiosError(error) && error.response?.data?.message) {
      error.message = error.response.data.message;
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
